import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged-out, obj=logged-in
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.startsWith("/r/") || path.startsWith("/s/") || path.startsWith("/wins/")) {
      setUser(false);
      return;
    }
    if (!localStorage.getItem("af_token") && (path === "/" || path === "/login" || path === "/register")) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("af_token", data.access_token);
      // Clean any stale impersonation state from a prior session.
      localStorage.removeItem("af_admin_token");
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (email, password, name, extra = {}) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { email, password, name, ...extra });
      if (data.access_token) localStorage.setItem("af_token", data.access_token);
      localStorage.removeItem("af_admin_token");
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("af_token");
    localStorage.removeItem("af_admin_token");
    setUser(false);
  };

  /** Admin → swap to a 30-min target-user token; stash original admin token. */
  const startImpersonation = async (targetUserId) => {
    const { data } = await api.post(`/admin/users/${targetUserId}/impersonate`);
    const adminToken = localStorage.getItem("af_token") || "";
    localStorage.setItem("af_admin_token", adminToken);
    localStorage.setItem("af_token", data.access_token);
    // Refresh /me with the new token so user state + impersonator metadata is live.
    await refresh();
    return data.user;
  };

  /** Restore the admin token and reload as the admin. */
  const exitImpersonation = async () => {
    const adminToken = localStorage.getItem("af_admin_token");
    if (!adminToken) return false;
    localStorage.setItem("af_token", adminToken);
    localStorage.removeItem("af_admin_token");
    await refresh();
    return true;
  };

  const impersonating = !!(user && user.impersonator);

  /** Adopt a session created outside of login/register (e.g. First Closer apply). */
  const applySession = (nextUser, accessToken) => {
    if (accessToken) localStorage.setItem("af_token", accessToken);
    setUser(nextUser);
  };

  return (
    <AuthCtx.Provider
      value={{
        user, error, setError, login, register, logout, refresh, applySession,
        startImpersonation, exitImpersonation, impersonating,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
